import type { ComponentCommonDays } from '@edwin-group/people-graphql';

const formatICSDate = (date: Date): string => {
  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
};

const formatGoogleDate = (date: Date): string => {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
};

const formatOutlookDate = (date: Date): string => {
  return date.toISOString().slice(0, 19);
};

export const generateGoogleCalendarUrl = (event: ComponentCommonDays): string => {
  const startDate = new Date(event.startDate);
  const endDate = event.endDate ? new Date(event.endDate) : new Date(startDate);

  if (event.wholeMonth) {
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);
  }

  if (endDate.getTime() === startDate.getTime()) {
    endDate.setHours(23, 59, 59);
  }

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.name,
    dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export const generateOutlookOnlineUrl = (event: ComponentCommonDays): string => {
  const startDate = new Date(event.startDate);
  const endDate = event.endDate ? new Date(event.endDate) : new Date(startDate);

  if (event.wholeMonth) {
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);
  }

  if (endDate.getTime() === startDate.getTime()) {
    endDate.setHours(23, 59, 59);
  }

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    startdt: formatOutlookDate(startDate),
    enddt: formatOutlookDate(endDate),
    allday: String(event.wholeMonth || false),
  });

  return `https://outlook.live.com/calendar/0/action/compose?${params.toString()}`;
};

export const generateMicrosoftUrl = (event: ComponentCommonDays): string => {
  const startDate = new Date(event.startDate);
  const endDate = event.endDate ? new Date(event.endDate) : new Date(startDate);

  startDate.setHours(8, 0, 0);
  endDate.setHours(12, 0, 0);
  if (event.wholeMonth) {
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);
  }

  if (endDate.getTime() === startDate.getTime()) {
    endDate.setHours(23, 59, 59);
  }

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    startdt: formatOutlookDate(startDate),
    enddt: formatOutlookDate(endDate),
    subject: event.name,
  });

  return `https://outlook.office.com/owa/?${params.toString()}`;
};

export const generateICSContent = (event: ComponentCommonDays): string => {
  const startDate = new Date(event.startDate);
  const endDate = event.endDate ? new Date(event.endDate) : new Date(startDate);

  if (event.wholeMonth) {
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);
  }

  if (endDate.getTime() === startDate.getTime()) {
    endDate.setHours(23, 59, 59);
  }

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Edwin People//WellbeingCalendar//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `DTSTART:${formatICSDate(startDate)}`,
    `DTEND:${formatICSDate(endDate)}`,
    `SUMMARY:${event.name}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return icsContent;
};

export const downloadCalendarEvent = (event: ComponentCommonDays): void => {
  const icsContent = generateICSContent(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.setAttribute('download', `${event.name.replace(/\s+/g, '-')}.ics`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
